
import {Component, Vue, Watch} from 'vue-property-decorator';
import {DocBook, getDocBook} from '@/service/kk-help-doc/doc-book';
import {hideLoading, showLoading, showWarning} from '@/utils/message';
import {DocSection} from '@/service/kk-help-doc/doc-section';
import DocSectionList, {DocSectionExt} from '@/views/doc/DocSectionList.vue';
import DocParagraphList from '@/views/doc/DocParagraphList.vue';
import {listDocParagraph} from '@/service/kk-help-doc/doc-paragraph';
import {matchKeyword} from '@/views/doc/search';

interface SearchResult {
  sectionPath: DocSection[];
  matchContents: string[];
}

@Component({
  components: {DocParagraphList, DocSectionList},
})
export default class DocBookDetail extends Vue {
  public book: DocBook | null = null;
  public selectedSections: DocSection[] | null = null;
  public initSectionId: string = '';
  public search: {
    keyword: string,
    results: SearchResult[],
    start: number,
    cost: number,
  } = {
    keyword: '',
    results: [],
    start: 0,
    cost: 0,
  };
  public mode: 'showDoc' | 'search' = 'showDoc';


  public created() {
    this.fetchData();
  }

  @Watch('$route')
  public watchRoute() {
    this.fetchData();
  }

  @Watch('search.keyword')
  public watchKeyword() {
    if (!this.search.keyword && this.mode === 'search') {
      this.mode = 'showDoc';
    }
  }

  public fetchData() {
    showLoading();
    Promise.resolve().then(async () => {
      const bookId = this.$route.params.id;
      if (!this.book || this.book.id !== bookId) {
        const book = await getDocBook(bookId);
        if (!book) {
          this.book = null;
          throw new Error('文档不存在');
        }
        this.book = book;
        document.title = book.name;
      }
      if (location.hash) {
        const sectionId = location.hash.startsWith('#') ? `${location.hash}`.substr(1) : location.hash;
        this.initSectionId = sectionId;
      }
    }).catch(showWarning)
        .finally(hideLoading);
  }

  public handleSelectSection(sections: DocSection[]) {
    this.selectedSections = sections;
    location.hash = sections[sections.length - 1].id;
  }

  public startSearch() {
    const keyWord = this.search.keyword.trim();
    if (!keyWord) {
      return;
    }
    Promise.resolve().then(async () => {
      const sectionListComponent = this.$refs.sectionList as any;
      const sectionMap: Map<string, DocSectionExt> = sectionListComponent.getSectionMap();
      this.search.results = [];
      this.search.start = new Date().getTime();
      this.search.cost = 0;
      this.mode = 'search';
      // 迭代，开始匹配
      const values = sectionMap.values();
      let val = values.next();
      while (!val.done) {
        this.search.cost = new Date().getTime() - this.search.start;
        if (this.mode !== 'search') {
          break;
        }
        const section = val.value;
        const paragraphs = await listDocParagraph(section.id);
        const matchContents: string[] = [];
        for (const paragraph of paragraphs) {
          if (paragraph.type === 'TEXT' && paragraph.content) {
            const result = matchKeyword(paragraph.content, keyWord);
            if (result && result.length) {
              matchContents.push(...result);
            }
          }
          if (paragraph.type === 'MARKDOWN' && paragraph.content) {
            const result = matchKeyword(paragraph.content, keyWord);
            if (result && result.length) {
              matchContents.push(...result);
            }
          }
          if (paragraph.type === 'RICHTEXT' && paragraph.content) {
            // 提取纯文本
            const div = document.createElement('div');
            div.innerHTML = paragraph.content;
            const result = matchKeyword(div.innerText, keyWord);
            if (result && result.length) {
              matchContents.push(...result);
            }
          }
        }
        if (matchContents.length) {
          // 获取章节的路径
          const ancestors: DocSection[] = sectionListComponent.getSectionAncestors(section);
          const path = [...ancestors, section];
          this.search.results.push({
            sectionPath: path,
            matchContents,
          });
        }
        val = values.next();
      }
    }).catch(showWarning);
  }

  public showSection(section: DocSection) {
    this.mode = 'showDoc';
    const sectionListComponent = this.$refs.sectionList as any;
    sectionListComponent.switchToSection(section.id);
  }
}
